import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { addInjury, getEmergencySetup, removeInjury } from "../api/emergencyApi";
import { detailBlock, escapeHtml, printDocument } from "../utils/print";

/**
 * The injury report on a medico-legal case (ER-MLC-006/007/008/009).
 *
 * Body part and wound type come from the hospital's configured lists rather
 * than free text, so reports read consistently across cases and years — which
 * is most of what a legal record is for.
 *
 * The empty state says outright that the visit cannot be closed without this,
 * because the alternative is a doctor discovering it at the point of trying to
 * discharge a patient.
 */
export default function InjuryReportCard({ visit, onChanged, disabled }) {
  const injuries = visit.injuries || [];
  const mlc = visit.mlc;

  const [vocab, setVocab] = useState(null);
  const [loadingVocab, setLoadingVocab] = useState(true);
  const [bodyPartId, setBodyPartId] = useState("");
  const [woundTypeId, setWoundTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadVocab = useCallback(async () => {
    setLoadingVocab(true);
    try {
      const setup = await getEmergencySetup();
      setVocab({ bodyParts: setup.body_parts || [], woundTypes: setup.wound_types || [] });
    } catch (e) {
      setError(e?.message || "Could not load the body-part and wound-type lists");
    } finally {
      setLoadingVocab(false);
    }
  }, []);

  useEffect(() => { loadVocab(); }, [loadVocab]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    run(async () => {
      await addInjury(visit.id, {
        bodyPartId,
        woundTypeId,
        description: description.trim(),
      });
      setBodyPartId("");
      setWoundTypeId("");
      setDescription("");
    });

  const print = () => {
    const opened = printDocument({
      title: "Injury Report",
      subtitle: `Medico-legal case ${mlc?.case_number || "—"}`,
      body: `
        <h2>Case identifiers</h2>
        ${detailBlock([
          ["Medico-legal case number", mlc?.case_number],
          ["Patient", visit.display_name],
          ["Visit number", visit.visit_number],
          ["Hospital identifier", visit.patient_id],
          ["Age / gender", `${visit.age ?? "not recorded"} / ${visit.gender || "—"}`],
          ["Chief complaint", visit.chief_complaint],
          ["Arrived", formatDateTime(visit.arrived_at)],
        ])}
        <h2>Injuries (${injuries.length})</h2>
        <ol>
          ${injuries
            .map(
              (i) =>
                `<li><strong>${escapeHtml(i.body_part)} — ${escapeHtml(i.wound_type)}.</strong>` +
                `${i.description ? " " + escapeHtml(i.description) : ""}` +
                `<br /><small>Recorded ${escapeHtml(formatDateTime(i.recorded_at))}` +
                `${i.recorded_by_name ? " by " + escapeHtml(i.recorded_by_name) : ""}</small></li>`
            )
            .join("")}
        </ol>`,
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  const canAdd = Boolean(bodyPartId && woundTypeId) && !busy;

  return (
    <IpdSectionCard
      title="Injury report"
      subtitle="Body part and wound type from the hospital's own lists; as many entries as needed"
      actions={
        injuries.length > 0 ? (
          <IpdButton variant="outlined" size="small" startIcon={<PrintIcon />} onClick={print}>
            Print
          </IpdButton>
        ) : null
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {injuries.length === 0 ? (
        <Box
          sx={{
            px: 2, py: 2, mb: 2,
            border: `1px dashed ${IPD_COLORS.warning.border}`,
            bgcolor: IPD_COLORS.warning.bg,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.warning.text }}>
            No injuries documented yet
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
            A medico-legal visit cannot be closed without an injury report — except for a
            patient brought in dead, where the treating team may document none.
          </Typography>
        </Box>
      ) : (
        <Box component="ol" sx={{ m: 0, mb: 2, pl: 0, listStyle: "none" }}>
          {injuries.map((i, index) => (
            <Box
              key={i.id}
              component="li"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
                px: 1.75, py: 1.25,
                mb: 1,
                border: `1px solid ${IPD_COLORS.border}`,
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {index + 1}. {i.body_part} — {i.wound_type}
                </Typography>
                {i.description && (
                  <Typography sx={{ mt: 0.3, fontSize: 13, color: IPD_COLORS.textPrimary }}>
                    {i.description}
                  </Typography>
                )}
                <Typography sx={{ mt: 0.3, fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  {formatDateTime(i.recorded_at)}
                  {i.recorded_by_name ? ` · ${i.recorded_by_name}` : ""}
                </Typography>
              </Box>
              {!disabled && (
                <Tooltip title="Remove this injury">
                  <IconButton
                    size="small"
                    disabled={busy}
                    aria-label={`Remove injury ${index + 1}`}
                    onClick={() => run(() => removeInjury(visit.id, i.id))}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!disabled && (
        <Box
          sx={{
            p: 1.75,
            bgcolor: IPD_COLORS.pageBg,
            border: `1px solid ${IPD_COLORS.border}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ ...ipdLabelSx, mb: 1.25 }}>Add an injury</Typography>

          {loadingVocab ? (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <TextField
                  select
                  value={bodyPartId}
                  onChange={(e) => setBodyPartId(e.target.value)}
                  label="Body part"
                  size="small"
                  required
                  sx={ipdElevatedInputSx}
                  SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                  helperText={
                    vocab?.bodyParts?.length ? "" : "Add body parts under Department Setup first"
                  }
                >
                  {(vocab?.bodyParts || []).map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  value={woundTypeId}
                  onChange={(e) => setWoundTypeId(e.target.value)}
                  label="Wound type"
                  size="small"
                  required
                  sx={ipdElevatedInputSx}
                  SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                  helperText={
                    vocab?.woundTypes?.length ? "" : "Add wound types under Department Setup first"
                  }
                >
                  {(vocab?.woundTypes || []).map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                label="Description"
                placeholder="Size, site, character of the wound…"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={ipdElevatedInputSx}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IpdButton startIcon={<AddIcon />} onClick={submit} disabled={!canAdd} loading={busy}>
                  Add injury
                </IpdButton>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </IpdSectionCard>
  );
}
